import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Icon, NewInlineModal, SectionHeader, Field } from 'views/components/General';
import ContentLoader from 'react-content-loader';
import PropTypes from 'prop-types';
import styles from './style.module.scss';

export default class Pagination extends Component {
  constructor(props) {
    super(props);
    const { currentPage } = props;
    this.state = {
      currentPage: Number(currentPage),
      searchPage: currentPage,
      showGoToPageInlineModal: false,
      showInvalidGoToPageError: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentPage !== this.props.currentPage) {
      this.setState({ currentPage: Number(this.props.currentPage) });
    }
  }

  getStartingPageNumber = () => {
    const { totalPages } = this.props;
    const { currentPage } = this.state;
    if (currentPage - 2 < 1) return 1;
    if (currentPage + 2 >= totalPages && totalPages >= 5) return totalPages - 4;
    return totalPages < 5 ? 1 : currentPage - 2;
  };

  renderPageNumbers = () => {
    const { totalPages } = this.props;
    const { currentPage } = this.state;
    const lastPageNumberIsShowing = currentPage + 4 > totalPages;
    const getLastPageNumber = currentPage > 2 ? currentPage - 2 + 4 : currentPage + 4;
    const pageNumbers = [];
    for (
      let pageNumber = this.getStartingPageNumber();
      pageNumber <= (lastPageNumberIsShowing ? totalPages : getLastPageNumber);
      pageNumber++
    ) {
      if (pageNumbers.length === 5) {
        return pageNumbers;
      } else {
        pageNumbers.push(
          <div
            className={`${styles['page']} ${currentPage === pageNumber ? styles['selected'] : ''}`}
            onClick={() => (pageNumber === currentPage ? null : this.selectPage(pageNumber))}
          >
            {pageNumber}
          </div>
        );
      }
    }
    return pageNumbers;
  };

  renderLoader = () => (
    <ContentLoader
      speed={2}
      width={839}
      height={60}
      viewBox="0 0 839 60"
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
    >
      <rect x="330" y="22" rx="3" ry="3" width="180" height="18" />
    </ContentLoader>
  );

  selectPage = page => {
    this.setState({
      currentPage: page,
      showGoToPageInlineModal: false,
    });
    this.props.onSelectPage(page);
  };

  renderGoToPageInlineModal = () => {
    if (this.props.totalPages <= 5) {
      return null;
    }
    return (
      <>
        <div className={styles['go-to-page-separator']} />
        <div
          ref={ref => (this.goToPageInlineModalPositioningRef = ref)}
          className={`${styles['go-to-page-button']} ${styles['page']}`}
          onClick={() => {
            this.setState(prevState => ({
              showGoToPageInlineModal: !prevState.showGoToPageInlineModal,
              searchPage: '',
              showInvalidGoToPageError: false,
            }));
          }}
        >
          <Icon regular type="ellipsis-h" />
        </div>
        <NewInlineModal
          minWidth={237}
          position="right"
          positionToRef={this.goToPageInlineModalPositioningRef}
          open={this.state.showGoToPageInlineModal}
          onClose={() => {
            const newPage = Math.abs(this.state.searchPage);
            let isValidPage = true;
            if (newPage <= 0) {
              isValidPage = false;
            }
            if (newPage > this.props.totalPages) {
              isValidPage = false;
            }
            if (newPage === this.state.currentPage) {
              isValidPage = false;
            }
            if (isNaN(this.state.searchPage)) {
              isValidPage = false;
            }
            if (isValidPage) {
              this.selectPage(newPage);
            } else {
              this.setState({ showGoToPageInlineModal: false });
            }
          }}
        >
          <NewInlineModal.Header>
            <SectionHeader noBorderTop>
              <FormattedMessage id="components.pagination.go-to-page" />
            </SectionHeader>
          </NewInlineModal.Header>
          <div className={styles['go-to-page-container']}>
            <Field.Number
              autoFocus
              error={this.state.showInvalidGoToPageError}
              value={this.state.searchPage}
              onChange={searchPage => {
                if (this.state.showInvalidGoToPageError) {
                  this.setState({ showInvalidGoToPageError: false });
                }
                this.setState({ searchPage });
              }}
              onKeyDown={({ key }) => {
                if (key === 'Enter' || key === 'Tab') {
                  const newPage = Math.abs(this.state.searchPage);
                  let isValidPage = true;
                  if (newPage <= 0) {
                    isValidPage = false;
                  }
                  if (newPage > this.props.totalPages) {
                    isValidPage = false;
                  }
                  if (newPage === this.state.currentPage) {
                    isValidPage = false;
                  }
                  if (isNaN(this.state.searchPage)) {
                    isValidPage = false;
                  }
                  if (isValidPage) {
                    this.selectPage(newPage);
                  } else {
                    this.setState({ showInvalidGoToPageError: true });
                  }
                }
              }}
            />
          </div>
        </NewInlineModal>
      </>
    );
  };

  render() {
    const { currentPage } = this.state;
    const { totalPages } = this.props;

    let classNames = [styles['pagination-container']];
    if (this.props.blue) {
      classNames = [...classNames, styles['blue']];
    }
    if (this.props.loading) return this.renderLoader();
    return (
      <div className={classNames.join(' ')} style={this.props.style}>
        {this.props.totalEntriesComponent == null ? null : (
          <div className={styles['total-entries']}>{this.props.totalEntriesComponent}</div>
        )}
        <div className={styles['pagination']}>
          {totalPages <= 5 ? null : (
            <div
              className={`${styles['page']} ${styles['previous']} ${
                currentPage === 1 ? styles['disabled'] : ''
              }`}
              onClick={() => (currentPage === 1 ? null : this.selectPage(1))}
            >
              <Icon regular type="angle-double-left" />
            </div>
          )}
          <div
            className={`${styles['page']} ${styles['previous']} ${
              currentPage === 1 ? styles['disabled'] : ''
            }`}
            onClick={() => (currentPage === 1 ? null : this.selectPage(currentPage - 1))}
          >
            <Icon regular type="angle-left" />
          </div>
          {this.renderPageNumbers()}
          <div
            className={`${styles['page']} ${currentPage === totalPages ? styles['disabled'] : ''}`}
            onClick={() => (currentPage === totalPages ? null : this.selectPage(currentPage + 1))}
          >
            <Icon regular type="angle-right"></Icon>
          </div>
          {totalPages <= 5 ? null : (
            <div
              className={`${styles['page']} ${currentPage === totalPages ? styles['disabled'] : ''}`}
              onClick={() => (currentPage === totalPages ? null : this.selectPage(totalPages))}
            >
              <Icon regular type="angle-double-right"></Icon>
            </div>
          )}
        </div>
        {this.renderGoToPageInlineModal()}
      </div>
    );
  }
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalEntriesComponent: PropTypes.node.isRequired,
  onSelectPage: PropTypes.func.isRequired,
  blue: PropTypes.bool,
  style: PropTypes.object,
};

Pagination.defaultProps = {
  currentPage: 1,
  style: null,
  blue: false,
  totalEntriesComponent: null,
};
